import axios, { AxiosInstance, AxiosError } from 'axios';
import { getIdToken } from '../utils/firebase';
import { normalizeErrorPayload } from '../utils/apiError';

// The billing engine (iadvocate-billing repo, Plan 2) is a SEPARATE Cloud Run
// service from the main API. When REACT_APP_BILLING_URL is unset (engine not
// deployed yet / current build), baseURL resolves to '' — every call below
// then rejects immediately (network error against a relative path with no
// dev server route), which UpgradeModal treats as "billing not configured"
// rather than crashing. This keeps the current deploy behavior-neutral until
// the engine ships (see deploy env plumbing note in .env.example).
const BILLING_URL = process.env.REACT_APP_BILLING_URL || '';

export const isBillingConfigured = (): boolean => BILLING_URL.length > 0;

const billingApi: AxiosInstance = axios.create({
  baseURL: BILLING_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Firebase bearer interceptor — mirrors api.ts. The engine verifies the same
// Firebase project's ID tokens (see plan Task 6). Harmless to attach on the
// public GET /plans call too.
billingApi.interceptors.request.use(
  async (config) => {
    const token = await getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

billingApi.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    normalizeErrorPayload(error.response?.data);
    if (process.env.NODE_ENV === 'development') {
      console.error('Billing API Error:', error.response?.status, error.config?.url);
    }
    return Promise.reject(error);
  },
);

// ── Wire shapes (engine/app/api/checkout.py + plans/subscriptions, Plan 2B) ──

export interface BillingPlan {
  plan_code: string;
  tier: string; // 'pro' | 'max' (and future tiers)
  billing_period: 'monthly' | 'annual';
  /** paise, GST-EXCLUSIVE catalog price (owner decision 2026-10-17: prices are "+ GST") */
  price_inr: number;
  /** paise, 18% GST on price_inr — computed by the engine; the UI never computes tax */
  gst_inr?: number;
  /** paise, what checkout actually charges = price_inr + gst_inr. Optional so an
   *  engine older than these fields still renders (the UI then says "+ GST"). */
  total_inr?: number;
  /** Whether buying this plan now sets up auto-renewal (a mandate). False for
   *  annual, and for monthly while the engine sells it PREPAID. Optional for
   *  an older engine. */
  auto_renews?: boolean;
}

/** Buyer details every GST tax invoice needs (engine GET/PUT /billing-profile). */
export interface BillingProfile {
  billing_name: string | null;
  state_code: string | null;
  gstin: string | null;
  /** required by the engine when gstin is set (CGST Rule 46(d)) */
  billing_address: string | null;
  /** GST state/UT code -> name; the one source of truth for the form's list */
  states: Record<string, string>;
}

export interface InvoiceSummary {
  id: string;
  number: string; // e.g. IAD/2026-27/00001
  issued_at: string;
  plan: string;
  gross_inr: number; // paise, total paid incl. GST
  gst_inr: number; // paise
  emailed: boolean;
}

export interface CheckoutSessionResponse {
  provider: string;
  psp_ref: string; // subscription_id or order_id
  checkout_payload: Record<string, unknown>; // fed straight to Razorpay Checkout JS
  plan_code: string;
  /** ISO timestamp of a DEFERRED first charge — set when a lawyer converts
   *  mid-trial on a monthly plan (Razorpay `start_at`). Null when charged at
   *  once. When set, nothing is pushed until that date, so the modal must not
   *  poll for activation (see UpgradeModal's 'scheduled' stage). Optional so
   *  an engine older than this field still type-checks as "charged now". */
  first_charge_at?: string | null;
}

export interface EngineSubscription {
  id: string;
  vertical: string;
  plan_code: string;
  /** pro|max from the engine's plan row. Optional: older engines omit it. */
  tier?: string | null;
  status: string; // trialing|active|past_due|suspended|paused|canceled|expired
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  /** True on the ONE row that speaks for its vertical (a live row if any,
   *  else the most recently changed terminal row). A subject can hold an
   *  expired old row beside a live new one (resubscribe, upgrade); the
   *  engine marks which one counts. Optional: older engines omit it. */
  authoritative?: boolean;
  /** True only with a live mandate. Annual, prepaid monthly and comps are
   *  false: they END at current_period_end unless renewed. Optional for an
   *  older engine (planExpiry.autoRenews falls back). */
  auto_renews?: boolean;
}

/** Structured 409/422/502/503 error bodies the engine returns for checkout. */
export interface BillingErrorDetail {
  code:
    | 'plan_not_purchasable'
    | 'live_subscription_exists'
    | 'plan_misconfigured'
    | 'checkout_unavailable'
    | 'checkout_not_configured'
    | 'billing_profile_required'
    | 'invoicing_not_configured'
    | 'credit_pack_not_purchasable'
    | 'no_live_subscription_for_topup'
    | 'subscription_not_active'
    | string;
  message?: string;
}

/** A purchasable top-up credit pack (engine GET /credit-packs?vertical=...,
 *  2026-10-29 redesign). Same price-field shape as BillingPlan — gst_inr/
 *  total_inr are always present on this engine version, kept optional here
 *  only for symmetry with BillingPlan's own older-engine fallback. */
export interface CreditPack {
  pack_code: string;
  vertical: string;
  credits: number;
  /** paise, GST-EXCLUSIVE catalog price */
  price_inr: number;
  /** paise, 18% GST on price_inr */
  gst_inr?: number;
  /** paise, what checkout actually charges = price_inr + gst_inr */
  total_inr?: number;
}

export interface TopupCheckoutSessionResponse {
  provider: string;
  psp_ref: string; // order_id — a top-up is always a one-shot Order
  checkout_payload: Record<string, unknown>; // fed straight to Razorpay Checkout JS
  pack_code: string;
}

export const billingApiClient = {
  /** GET /plans?vertical=... — public (no auth required, but the interceptor
   *  attaches a bearer if one is available; harmless either way). */
  getPlans: async (vertical: string): Promise<BillingPlan[]> => {
    const response = await billingApi.get('/plans', { params: { vertical } });
    return response.data;
  },

  /** POST /checkout-sessions {plan_code} — Firebase-authenticated. 60s timeout
   *  (house pattern from firApi.ts): this call round-trips to Razorpay to
   *  create/cache a plan + subscription/order server-side, which is slower
   *  than the default 30s budget on api.ts. */
  createCheckout: async (planCode: string): Promise<CheckoutSessionResponse> => {
    const response = await billingApi.post(
      '/checkout-sessions',
      { plan_code: planCode },
      { timeout: 60000 },
    );
    return response.data;
  },

  /** POST /subscriptions/{id}/upgrade {target_plan_code} — Firebase-
   *  authenticated, owner-checked. Returns the SAME CheckoutSessionResponse
   *  shape createCheckout returns — the engine's upgrade route builds its
   *  response from the identical CheckoutSessionResponseBody model, so the
   *  Razorpay Checkout JS integration point is identical for both flows.
   *  `first_charge_at` will almost always be set here (credit-as-free-time
   *  defers the first charge to the end of the unused current period) —
   *  unlike a fresh checkout, where it is usually null. That does NOT mean
   *  access is deferred too: the upgraded tier activates immediately on the
   *  mandate webhook; only the bill is deferred. See UpgradeModal's
   *  upgrade-mode 'scheduled' stage copy. Same 60s override as
   *  createCheckout — this also round-trips to Razorpay to create/cache a
   *  subscription. */
  upgradeSubscription: async (
    subscriptionId: string,
    targetPlanCode: string,
  ): Promise<CheckoutSessionResponse> => {
    const response = await billingApi.post(
      `/subscriptions/${subscriptionId}/upgrade`,
      { target_plan_code: targetPlanCode },
      { timeout: 60000 },
    );
    return response.data;
  },

  /** GET /credit-packs?vertical=... — public, mirrors getPlans. Only packs
   *  with is_active + price_inr > 0 are ever returned (same purchasability
   *  gate the engine's checkout itself enforces). */
  getCreditPacks: async (vertical: string): Promise<CreditPack[]> => {
    const response = await billingApi.get('/credit-packs', { params: { vertical } });
    return response.data;
  },

  /** POST /topup-checkout-sessions {pack_code} — Firebase-authenticated. Same
   *  60s override as createCheckout: this also round-trips to Razorpay to
   *  create a one-shot Order. Requires an existing LIVE paid subscription in
   *  the pack's vertical — 409 `no_live_subscription_for_topup` otherwise. */
  createTopupCheckout: async (packCode: string): Promise<TopupCheckoutSessionResponse> => {
    const response = await billingApi.post(
      '/topup-checkout-sessions',
      { pack_code: packCode },
      { timeout: 60000 },
    );
    return response.data;
  },

  /** GET /billing-profile — the buyer's invoice details + the GST state list. */
  getBillingProfile: async (): Promise<BillingProfile> => {
    const response = await billingApi.get('/billing-profile');
    return response.data;
  },

  /** PUT /billing-profile — 422 with a `code` (billing_name_invalid,
   *  state_code_invalid, gstin_format, gstin_checksum, gstin_state_mismatch). */
  saveBillingProfile: async (body: {
    billing_name: string;
    state_code: string;
    gstin?: string | null;
    billing_address?: string | null;
  }): Promise<BillingProfile> => {
    const response = await billingApi.put('/billing-profile', body);
    return response.data;
  },

  /** GET /invoices — the caller's own tax invoices, newest first. */
  getMyInvoices: async (): Promise<InvoiceSummary[]> => {
    const response = await billingApi.get('/invoices');
    return response.data;
  },

  /** GET /invoices/{id}/pdf — fetched with the bearer (a plain link would
   *  carry no auth), then handed to the browser as a download. */
  downloadInvoicePdf: async (invoice: InvoiceSummary): Promise<void> => {
    const response = await billingApi.get(`/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `iAdvocate-invoice-${invoice.number.replace(/\//g, '-')}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  /** GET /subscriptions/me — Firebase-authenticated. */
  getMySubscriptions: async (): Promise<EngineSubscription[]> => {
    const response = await billingApi.get('/subscriptions/me');
    return response.data;
  },

  /** POST /subscriptions/{id}/cancel — schedules cancellation at period end.
   *
   *  Does NOT end access now, and makes NO call to Razorpay: the engine only
   *  flips `cancel_at_period_end`. That is deliberate — Razorpay has no
   *  un-cancel API, so the real PSP cancel is deferred to the period-end cron
   *  precisely to keep `uncancelSubscription` below possible. 409
   *  `subscription_not_live` if the subscription isn't in a live state. */
  cancelSubscription: async (subscriptionId: string): Promise<EngineSubscription> => {
    const response = await billingApi.post(`/subscriptions/${subscriptionId}/cancel`);
    return response.data;
  },

  /** POST /subscriptions/{id}/uncancel — "I changed my mind", valid any time
   *  before the period-end sweep fires. 409 `cancel_not_pending` if no
   *  cancellation was scheduled. */
  uncancelSubscription: async (subscriptionId: string): Promise<EngineSubscription> => {
    const response = await billingApi.post(`/subscriptions/${subscriptionId}/uncancel`);
    return response.data;
  },

  /** POST /subscriptions/{id}/cancel-now — HARD cancel: the engine cancels the
   *  mandate at Razorpay FIRST, then transitions the subscription to terminal
   *  `canceled`. Unlike `cancelSubscription` above this is NOT reversible and
   *  there is no un-cancel — the mandate is dead at the PSP. It exists so a
   *  lawyer whose payment failed can subscribe again: `past_due`/`suspended`
   *  count as live and block a new checkout until the row goes terminal.
   *  502 `cancel_unavailable` if the PSP cancel could not be confirmed — the
   *  subscription is then unchanged and the call is worth retrying. */
  cancelSubscriptionNow: async (subscriptionId: string): Promise<EngineSubscription> => {
    const response = await billingApi.post(`/subscriptions/${subscriptionId}/cancel-now`);
    return response.data;
  },
};

export default billingApi;
